import React, { ReactNode } from 'react';
import { linkTo } from '@99designs/routing';
import { Site } from '@99designs/site-navigation/lib/Site';
import { withSkipLink } from '../components/Step/withSkipLink';
import { BrandingStageStepInputs } from './BrandingStageStep';
import { IndustryStepHeading, IndustryStepInputs } from './IndustryStep';
import { LaunchPage } from './LaunchPage';
import { LogoStepInputs } from './LogoStep';
import { ProductPreferencesStepInputs } from './ProductPreferencesStep';
import { PromotionStepInputs } from './PromotionStep';
import { RecommendationPage } from './RecommendationPage';
import {
    STEP_BRANDING_STAGE,
    STEP_INDUSTRY,
    STEP_LOGO,
    STEP_PRODUCT_PREFERENCES,
    STEP_PROMOTION,
} from './constants';
import { stepCompleted } from './utils/tracking';
import type { CurrencyCode } from '@99designs/i18n';
import type { Role } from '@99designs/site-navigation/consts';
import type { Config, GenericStepData, Step } from '../components/Onboarder/Config';
import type { BrandingStageValue } from './BrandingStageStep';
import type { IndustryValue } from './IndustryStep';
import type { LogoValue } from './LogoStep';
import type { ProductPreferences } from './ProductPreferencesStep';
import type { PromotionValue } from './PromotionStep';

export interface SelectedValues extends GenericStepData {
    [STEP_BRANDING_STAGE]?: BrandingStageValue;
    [STEP_INDUSTRY]?: IndustryValue;
    [STEP_PROMOTION]?: PromotionValue;
    [STEP_LOGO]?: LogoValue;
    [STEP_PRODUCT_PREFERENCES]?: ProductPreferences[];
}

const steps = (): Step<SelectedValues>[] => {
    const steps: Step<SelectedValues>[] = [
        {
            key: STEP_BRANDING_STAGE,
            title: __('Which best describes your branding needs?'),
            Heading: () => <span>{__('Which best describes your branding needs?')}</span>,
            Component: withSkipLink(BrandingStageStepInputs),
            isSkippable: true,
            canGoToNextStep: (data: SelectedValues) => Boolean(data[STEP_BRANDING_STAGE]),
            trackCompleted: (data: SelectedValues, stepNumber: number) =>
                stepCompleted(STEP_BRANDING_STAGE, data, stepNumber),
        },
        {
            key: STEP_INDUSTRY,
            title: __('My business is in this industry'),
            Heading: (props) => <IndustryStepHeading {...props} />,
            Component: withSkipLink(IndustryStepInputs),
            isSkippable: true,
            canGoToNextStep: (data: SelectedValues) => Boolean(data[STEP_INDUSTRY]),
            trackCompleted: (data: SelectedValues, stepNumber: number) =>
                stepCompleted(STEP_INDUSTRY, data, stepNumber),
        },
        {
            key: STEP_PROMOTION,
            title: __('How will you promote your brand?'),
            Heading: () => <span>{__('How will you promote your brand?')}</span>,
            Component: withSkipLink(PromotionStepInputs),
            isSkippable: true,
            canGoToNextStep: (data: SelectedValues) => Boolean(data[STEP_PROMOTION]),
            trackCompleted: (data: SelectedValues, stepNumber: number) =>
                stepCompleted(STEP_PROMOTION, data, stepNumber),
        },
        {
            key: STEP_LOGO,
            title: __('Do you need a logo?'),
            Heading: () => <span>{__('Do you need a logo?')}</span>,
            Component: LogoStepInputs,
            canGoToNextStep: (data: SelectedValues) => Boolean(data[STEP_LOGO]),
            trackCompleted: (data: SelectedValues, stepNumber: number) =>
                stepCompleted(STEP_LOGO, data, stepNumber),
        },
        {
            key: STEP_PRODUCT_PREFERENCES,
            title: __('What else do you need for your brand?'),
            Heading: () => <span>{__('What else do you need for your brand?')}</span>,
            Component: withSkipLink(ProductPreferencesStepInputs),
            canGoToNextStep: (data: SelectedValues) => Boolean(data[STEP_PRODUCT_PREFERENCES]),
            trackCompleted: (data: SelectedValues, stepNumber: number) =>
                stepCompleted(STEP_PRODUCT_PREFERENCES, data, stepNumber),
        },
    ];
    return steps;
};

export const stepIsValid = (index: number): boolean => {
    return !!steps()[index];
};

export const config = (
    role: Role,
    site: Site,
    exchangeRate: number,
    Layout: React.ComponentType<{ site: Site; role: Role; children: ReactNode }>
): Config<SelectedValues> => ({
    steps,
    currency: site.siteConfig['currency'] as CurrencyCode,
    exchangeRate,
    basePath: linkTo.brandingGetStarted(),
    Launcher: LaunchPage,
    Recommendation: () => <RecommendationPage role={role} site={site} Layout={Layout} />,
    pageTitle: __('Compare branding design solutions'),
    storageKeyPrefix: 'guidedBrandingProductRecommender',
    stepsResponse: () => [],
});
